import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowClockwise } from "@phosphor-icons/react";

const ASPECTS = [
  { l: "Free", v: null }, { l: "1:1", v: 1 }, { l: "4:3", v: 4 / 3 },
  { l: "3:2", v: 3 / 2 }, { l: "16:9", v: 16 / 9 }, { l: "9:16", v: 9 / 16 },
];

async function getCroppedBlob(src, area, fileName) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const f = new File([blob], fileName || "cropped.jpg", { type: "image/jpeg" });
      resolve(f);
    }, "image/jpeg", 0.92);
  });
}

export default function CropModal({ file, onCancel, onSave }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(null);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, px) => setAreaPx(px), []);

  const save = async () => {
    if (!areaPx) { onCancel(); return; }
    setBusy(true);
    const f = await getCroppedBlob(src, areaPx, file.name.replace(/\.(heic|heif|png|webp)$/i, ".jpg"));
    setBusy(false);
    onSave(f);
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-neutral-950" data-testid="crop-modal">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="font-display text-sm font-bold">Crop photo</span>
        <button data-testid="crop-close" onClick={onCancel} className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/10"><X size={18} weight="bold" /></button>
      </div>
      <div className="relative flex-1">
        <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect || undefined}
          onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} restrictPosition={false} />
      </div>
      <div className="space-y-3 bg-black/80 px-4 py-4">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {ASPECTS.map((a) => (
            <button key={a.l} data-testid={`crop-aspect-${a.l}`} onClick={() => setAspect(a.v)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                aspect === a.v ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/20"}`}>{a.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <ArrowClockwise size={16} className="text-white/70" />
          <input type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#F27A54]" data-testid="crop-zoom" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="h-9 rounded-md border-white/30 bg-transparent text-white hover:bg-white/10" onClick={onCancel} data-testid="crop-cancel">Cancel</Button>
          <Button className="h-9 gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" onClick={save} disabled={busy} data-testid="crop-save">
            <Check size={15} weight="bold" /> {busy ? "Cropping…" : "Apply crop"}
          </Button>
        </div>
      </div>
    </div>
  );
}
